'use client';
import NextLink from 'next/link';
import { FC } from 'react';
import {
  ChevronRight,
  MailOutline,
  PhoneFilled,
} from '@eqi-investimentos/icons/web';
import {
  Button,
  Flex,
  Skeleton,
  Spinner,
  Stack,
  Text,
} from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useCreateSFOpportunity } from '@/modules/Customer/http/client/mutations/useCreateSFOpportunity';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useGetCustomerAdvisor } from '@/modules/Customer/http/client/useGetCustomerAdvisor';
import { Icon } from '@/components/elements/Icon';
import { Thumb } from '@/components/elements/Thumb';
import { sanitizeNumber } from '@/utils/format';
import { IfRender } from '@/utils/jsx';
import { formatPhoneNumber } from '@/utils/masks';
import { formatAdvisorWhatsAppLink } from '@/utils/messenger';

export const CardMyAdvisor: FC<CardMyAdvisorProps> = ({
  hasAdvisor = false,
  opportunity = { action: '', product: '' },
  variant = 'desktop',
}) => {
  const t = useT();
  const disableHref = hasAdvisor;

  const { mutate: sendClickEvent } = useSendClickEvent();
  const { mutate: createSFOpportunity, isPending } = useCreateSFOpportunity();

  const { data: advisor, isFetching } = useGetCustomerAdvisor();

  const handleOpportunity = async () => {
    if (!opportunity || isPending) return;
    createSFOpportunity(opportunity);

    sendClickEvent({
      label:
        salesforceOpportunityConfig.sendClickEvent.myAdvisor.label(
          '/minha-assessoria'
        ),
    });

    const message = `Olá ${advisor?.advisorName.split(' ')[0]}, estou no EQI+ e gostaria de tirar uma dúvida `;

    const url = formatAdvisorWhatsAppLink({
      message: message,
      number: sanitizeNumber(advisor?.advisorMobilePhone ?? ''),
    });

    window.open(url, '_blank');
  };

  if (isFetching) {
    return <Skeleton w="full" h="8.5625rem" borderRadius="base" />;
  }

  if (!advisor?.advisorName) {
    return <></>;
  }

  if (variant === 'mobile') {
    return (
      <Flex
        p="m"
        background="background.brandLight"
        w="full"
        justify="space-between"
        align="center"
        cursor="pointer"
        onClick={handleOpportunity}
        position="relative"
        borderRadius="xs"
        pointerEvents={isPending ? 'none' : 'auto'}
      >
        <IfRender condition={isPending}>
          <Flex
            background="transparency.high"
            h="full"
            w="full"
            position="absolute"
            left={0}
            top={0}
            align="center"
            justify="center"
            borderRadius="xs"
            cursor="not-allowed"
          >
            <Spinner zIndex={99} />
          </Flex>
        </IfRender>
        <Flex gap="s">
          <Thumb
            src={advisor.photo || '/assets/images/placeholder_my_advisor.svg'}
            width={48}
            height={48}
            alt={
              advisor.photo
                ? 'foto do assessor'
                : 'icone de um atentender com headphone'
            }
          />
          <Stack flexDir="column" gap="3xs">
            <Text
              size="l"
              fontWeight="light"
              color="content.primary"
              whiteSpace="nowrap"
            >
              {t('attributes.myConsultancy')}
            </Text>
            <Text
              size="xl"
              color="content.primary"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {advisor.advisorName}
            </Text>
          </Stack>
        </Flex>
        <Icon iconName="ChevronRight" color="content.brand" w={24} h={24} />
      </Flex>
    );
  }

  return (
    <Flex
      flexDir="column"
      gap="m"
      p="m"
      w="full"
      bg="background.brandLight"
      borderRadius="xs"
      overflow="hidden"
    >
      <Flex
        as={NextLink}
        href={disableHref ? '#' : '/minha-assessoria'}
        cursor={disableHref ? 'initial' : 'pointer'}
        w="full"
        justifyContent="space-between"
        alignItems="center"
        gap="s"
      >
        <Flex alignItems="center" gap="s">
          <Thumb
            src={advisor.photo || '/assets/images/placeholder_my_advisor.svg'}
            width={48}
            height={48}
            alt={
              advisor.photo
                ? 'foto do assessor'
                : 'icone de um atentender com headphone'
            }
          />
          <Flex flexDir="column" gap="3xs">
            <Text size="l">{t('attributes.myConsultancy')}</Text>
            <Text
              size="l"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {advisor.advisorName}
            </Text>
          </Flex>
        </Flex>
        <IfRender condition={hasAdvisor}>
          <ChevronRight w={24} h={24} color="content.brand" />
        </IfRender>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" gap="m">
        <Flex
          flexDir="column"
          gap="s"
          color="content.secondary"
          sx={{
            svg: { mt: '2xs' },
          }}
        >
          <IfRender condition={!!advisor.advisorEmail}>
            <Flex justifyContent="center" gap="xs">
              <MailOutline w={16} h={16} color="content.brand" />
              <Text size="l" gap="xs" maxW="15rem" noOfLines={2}>
                {advisor.advisorEmail}
              </Text>
            </Flex>
          </IfRender>
          <IfRender condition={!!advisor.advisorMobilePhone}>
            <Flex justifyContent="flex-start" gap="xs">
              <PhoneFilled w={16} h={16} color="content.brand" />
              <Text gap="xs" size="l">
                {formatPhoneNumber(advisor.advisorMobilePhone ?? '')}
              </Text>
            </Flex>
          </IfRender>
        </Flex>
        <Button size="sm" w="full" maxW="6rem" onClick={handleOpportunity}>
          {t('attributes.toTalk')}
        </Button>
      </Flex>
    </Flex>
  );
};

type CardMyAdvisorProps = {
  hasAdvisor?: boolean;
  opportunity?: { action: string; product?: string; description?: string };
  variant?: 'desktop' | 'mobile';
};
